import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react"
import { Outlet, useBlocker } from "react-router"
import { GlobalNavSidebar } from "panels/global-nav/GlobalNavSidebar"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { ScrollableArea } from "@nova/component-scrollable-area"
import classNames from "classnames"
import {
    useConfirmationModalContext,
    getGenericConfirmationModalComponent,
} from "features/modals/WorkspaceConfirmationModalV2"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { useWorkspaceInitializer } from "./initializers"
import { BannerContainer } from "themes/default-tailwind/notifications/BannerContainer"
import { showGlobalModal } from "runlevel/actions"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { SystemInitializingContainer } from "themes/default-tailwind/panels"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { selectUserID } from "user/profile/selectors"
import Page403 from "./Page403"

export function WorkspaceGate() {
    const { unauthorized, ready } = useWorkspaceInitializer()
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const userId = useAppSelector(selectUserID)!
    fileManagementApi.useCredentialManager({ userId, orgId: workspaceId })
    if (unauthorized) return <Page403 />
    if (!workspaceId || !ready) return <SystemInitializingContainer />
    return (
        <div className="bg-gray-900 w-full h-screen flex flex-col">
            <BannerContainer />
            <div className="flex relative h-full">
                <div className="z-20">
                    <GlobalNavSidebar />
                </div>
                <div className="h-full grow py-2 pr-2 z-10 min-w-0">
                    <div className="h-full w-full border border-gray-700 rounded-lg overflow-hidden bg-gray-800 text-white flex flex-col">
                        <StripeCallback />
                        <Outlet />
                    </div>
                </div>
            </div>
        </div>
    )
}

const StripeCallback = () => {
    const dispatch = useAppDispatch()
    const searchParams = new URLSearchParams(window.location.search)
    const paymentSuccess = searchParams.get("session_id")
    useEffect(() => {
        if (paymentSuccess) {
            dispatch(initializeCenterModalForComponentAction(ModalNames.PaymentSuccessModal))
            dispatch(showGlobalModal())
        }
    }, [dispatch, paymentSuccess])
    return null
}

export function getGenericWorkspacePage({
    Header,
    Content,
    maxWidth = 1100,
}: {
    Header: FC
    Content: FC
    maxWidth?: number | "unbound"
}) {
    const GenericWorkspacePage = () => {
        return (
            <div
                className="flex flex-col w-full h-full flex-1"
                style={{
                    maxWidth: maxWidth === "unbound" ? undefined : maxWidth + 34,
                }}
            >
                <div className="h-[74px] min-h-[74px] overflow-hidden w-full px-12">
                    <Header />
                </div>
                <ScrollableArea className="w-full h-full px-6 lg:px-12">
                    <Content />
                </ScrollableArea>
            </div>
        )
    }
    return GenericWorkspacePage
}

export function getGenericWorkspacePageWithoutHeader({
    Content,
    maxWidth,
}: {
    Content: FC
    maxWidth?: number | string
}) {
    const GenericWorkspacePageWithoutHeader = () => {
        return (
            <div
                className="w-full"
                style={{
                    maxWidth,
                }}
            >
                <ScrollableArea className="w-full h-full px-4">
                    <Content />
                </ScrollableArea>
            </div>
        )
    }
    return GenericWorkspacePageWithoutHeader
}

export function getGenericWorkspacePageHeader({
    title,
    border = false,
    leftItems = [],
    rightItems = [],
}: {
    title: string
    border?: boolean
    leftItems?: FC[]
    rightItems?: FC[]
}) {
    const GenericWorkspacePageHeader = () => {
        return (
            <div
                className={classNames("w-full h-full flex justify-between items-center", {
                    "border-b border-b-gray-700": border,
                })}
            >
                <div className="flex gap-2 items-center">
                    {leftItems.map((Item, i) => (
                        <Item key={i} />
                    ))}
                    <div className="text-[1.375rem] telegraf">{title}</div>
                </div>
                <div className="flex gap-2 items-center">
                    {rightItems.map((Item, i) => (
                        <Item key={i} />
                    ))}
                </div>
            </div>
        )
    }
    return GenericWorkspacePageHeader
}

export function getGenericWorkspacePageContentWithFooter({
    Content,
    Footer,
    scrollable = true,
}: {
    Content: FC
    Footer: FC
    scrollable?: boolean
}) {
    const GenericWorkspacePageContentWithFooter = () => {
        return (
            <div className="w-full h-full pb-6">
                <div className="w-full h-full rounded-lg border border-gray-700 flex flex-col">
                    {scrollable ? (
                        <ScrollableArea className="grow" shouldPreventScrollbarShifting>
                            <div className="p-6 h-full">
                                <Content />
                            </div>
                        </ScrollableArea>
                    ) : (
                        <div className="grow p-6 overflow-hidden flex">
                            <Content />
                        </div>
                    )}
                    <div className="px-6">
                        <div className="h-[64px] min-h-[64px] border-t border-t-gray-700">
                            <Footer />
                        </div>
                    </div>
                </div>
            </div>
        )
    }
    return GenericWorkspacePageContentWithFooter
}

export function getGenericSaveableWorkspacePageContent({
    Content,
    Footer,
    scrollable,
}: {
    Content: FC<{ setHasUnsavedChanges?: Dispatch<SetStateAction<boolean>> }>
    Footer: FC<{ setHasUnsavedChanges?: Dispatch<SetStateAction<boolean>> }>
    scrollable?: boolean
}) {
    const GenericSaveableWorkspacePageContent = () => {
        const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
        const ContentWithContext = useMemo(() => {
            const ContentWithContext = () => <Content setHasUnsavedChanges={setHasUnsavedChanges} />
            return ContentWithContext
        }, [])
        const FooterWithContext = useMemo(() => {
            const FooterWithContext = () => <Footer setHasUnsavedChanges={setHasUnsavedChanges} />
            return FooterWithContext
        }, [])
        const ContentComponent = useMemo(
            () =>
                getGenericWorkspacePageContentWithFooter({
                    Content: ContentWithContext,
                    Footer: FooterWithContext,
                    scrollable,
                }),
            [ContentWithContext, FooterWithContext]
        )
        const confirmationModal = useConfirmationModalContext()
        const blocker = useBlocker(
            ({ currentLocation, nextLocation }) =>
                hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
        )
        useEffect(() => {
            if (blocker.state === "blocked" && !confirmationModal.isOpen) {
                confirmationModal
                    .confirm({
                        RenderComponent: getGenericConfirmationModalComponent({
                            title: "Discard Changes?",
                            message:
                                "Your unsaved changes will be discarded, do you want to proceed?",
                            confirmText: "Discard Changes",
                        }),
                    })
                    .then((res) => {
                        if (res) {
                            blocker.proceed()
                        } else {
                            blocker.reset()
                        }
                    })
            }
        }, [blocker, blocker.state, confirmationModal])
        return <ContentComponent />
    }
    return GenericSaveableWorkspacePageContent
}
